// Domain primitives — branded at compile time, validated in one place (modules-and-api.md §0.4, §2.5).
import { z } from "zod";

/** Integer minor units of ZAR. Never a float, never a string. */
export type Cents = number & { readonly __cents: true };
/** Integer basis points. 1500 = 15.00%. */
export type Bp = number & { readonly __bp: true };
/** Quantity in thousandths of a unit. 1000 = 1.0. */
export type Milli = number & { readonly __milli: true };
/** ISO-8601 date in branch-local terms: '2026-09-10'. Field names end in `Date`. */
export type LocalDate = string & { readonly __localDate: true };
/** ISO-8601 instant in UTC: '2026-09-10T09:14:22.001Z'. Field names end in `At`. */
export type Instant = string & { readonly __instant: true };
export type Ulid = string & { readonly __ulid: true };

export const cents = (n: number): Cents => {
  if (!Number.isInteger(n)) throw new TypeError(`cents must be an integer, got ${n}`);
  return n as Cents;
};
export const bp = (n: number): Bp => {
  if (!Number.isInteger(n)) throw new TypeError(`basis points must be an integer, got ${n}`);
  return n as Bp;
};

const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;
export const UlidSchema = z.string().regex(ULID_RE, "must be a ULID").transform((s) => s as Ulid);
export const CentsSchema = z.number().int().transform((n) => n as Cents);
export const NonNegativeCentsSchema = z.number().int().min(0).transform((n) => n as Cents);
export const BpSchema = z.number().int().transform((n) => n as Bp);
export const MilliSchema = z.number().int().transform((n) => n as Milli);
export const LocalDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "must be YYYY-MM-DD").transform((s) => s as LocalDate);
export const InstantSchema = z.string().datetime({ offset: false }).transform((s) => s as Instant);

/** 2–4 uppercase alphanumerics, unique per organisation, immutable once a number has been issued (TEN-16, R-11). */
export const BranchCodeSchema = z.string().regex(/^[A-Z0-9]{2,4}$/, "2 to 4 uppercase letters or digits");
/** `NAU001/0` — main member `/0`, dependants `/1`… (BR-01, BR-02, BR-03). */
export const AccountCodeSchema = z.string().regex(/^[A-Z]{3}[0-9]{3,}\/[0-9]+$/, "account code like NAU001/0");

/** South African ID number: 13 digits with a valid Luhn check digit (BR-06). */
export function isValidSaId(id: string): boolean {
  if (!/^\d{13}$/.test(id)) return false;
  let sum = 0;
  for (let i = 0; i < 13; i++) {
    let d = Number(id[12 - i]);
    if (i % 2 === 1) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  return sum % 10 === 0;
}
export const SaIdNumberSchema = z.string().refine(isValidSaId, "not a valid South African ID number");

export const EmailSchema = z.string().trim().toLowerCase().email().max(254);

// --- Sprint 4 formats: SKUs, codes, barcodes, serial numbers, whole units (catalogue, inventory, purchasing, imports) ------
// Codes are TEXT, never numbers (CLAUDE.md): a leading zero survives and a long value never loses precision.

/** BR-CATALOGUE-003: 1–40 letters, digits and `-` `_` `.` `/`, without spaces; spaces around it are trimmed. */
export const SKU_MAX_LENGTH = 40;
export const SkuSchema = z.string().trim().min(1).max(SKU_MAX_LENGTH)
  .regex(/^[A-Za-z0-9._/-]+$/, "letters, digits and - _ . / only, without spaces");

/** BR-CATALOGUE-012: a NAPPI code, 1–20 letters and digits, kept as text (Medprax supplies them, D55). */
export const NappiCodeSchema = z.string().trim().min(1).max(20).regex(/^[A-Za-z0-9]+$/, "letters and digits only");
/** BR-CATALOGUE-012: a tariff code, 2–12 letters and digits, kept as text and resolved by the string, never a link (D30, D55). */
export const TariffCodeSchema = z.string().trim().min(2).max(12).regex(/^[A-Za-z0-9]+$/, "letters and digits only");

/**
 * The GS1 check digit of the digits before it (BR-CATALOGUE-013): weights 3 and 1 alternately from the digit next to the
 * check digit, and the check digit takes the sum to the next multiple of ten. The same rule serves EAN-13 (the first twelve
 * weighted 1 and 3 from the left: `600960100001` sums 43, check 7), UPC-A (the first eleven weighted 3 and 1: `01234567890`
 * sums 85, check 5) and EAN-8.
 */
export function gs1CheckDigit(body: string): number {
  if (!/^\d+$/.test(body)) throw new TypeError(`a GS1 code is digits only, got "${body}"`);
  let sum = 0;
  for (let i = 0; i < body.length; i++) sum += Number(body[body.length - 1 - i]) * (i % 2 === 0 ? 3 : 1);
  return (10 - (sum % 10)) % 10;
}
/** Digits only, and the last digit is the GS1 check digit of the others. */
export function hasValidGs1CheckDigit(code: string): boolean {
  return /^\d{2,}$/.test(code) && gs1CheckDigit(code.slice(0, -1)) === Number(code.slice(-1));
}
/** A supplier EAN (BR-CATALOGUE-013): 13 digits with a valid check digit. */
export const isValidEan13 = (code: string): boolean => /^\d{13}$/.test(code) && hasValidGs1CheckDigit(code);
/** A supplier UPC (BR-CATALOGUE-013): 12 digits with the UPC-A check digit. */
export const isValidUpcA = (code: string): boolean => /^\d{12}$/.test(code) && hasValidGs1CheckDigit(code);
/**
 * An EAN-8: 8 digits with a valid check digit. BR-CATALOGUE-013 names no EAN-8 kind — an 8-digit pack code is stored as an
 * alternate code and its digit is not checked — so this is for the day a kind of its own is asked for (UC-CATALOGUE-007).
 */
export const isValidEan8 = (code: string): boolean => /^\d{8}$/.test(code) && hasValidGs1CheckDigit(code);

/**
 * GS1's restricted-circulation prefix (BR-CATALOGUE-015, BR-TENANCY-035): the practice's own generated EAN-13s begin `20`,
 * and a 13-digit code beginning `20` is only ever generated (BR-CATALOGUE-013, `reserved_prefix`). `formatGeneratedBarcode`
 * (sequences) makes them.
 */
export const GENERATED_BARCODE_PREFIX = "20";
export const isReservedBarcodePrefix = (code: string): boolean => /^\d{13}$/.test(code) && code.startsWith(GENERATED_BARCODE_PREFIX);

/**
 * BR-INVENTORY-009: a serial number is `YYYY-MM-DD-NNN` — the date the unit arrived and a running number per organisation per
 * date, at least three digits and four past 999 (`2026-09-07-1000`). Only the tenant sequencer issues that shape: no barcode
 * has it (`serial_shape`, BR-CATALOGUE-013) and no supplier's serial may (`reserved_format`, BR-INVENTORY-009).
 */
export const SERIAL_NUMBER_PATTERN = /^\d{4}-\d{2}-\d{2}-\d{3,}$/;
export const isSerialNumberShape = (code: string): boolean => SERIAL_NUMBER_PATTERN.test(code);
/** Renders a serial number from its date and running number: `formatSerialNumber('2026-09-07', 1)` = `2026-09-07-001`. */
export function formatSerialNumber(date: string, sequence: number): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new RangeError(`a serial's date is YYYY-MM-DD, got "${date}"`);
  if (!Number.isSafeInteger(sequence) || sequence < 1) throw new RangeError(`a serial's running number is a whole number from 1, got ${sequence}`);
  return `${date}-${String(sequence).padStart(3, "0")}`;
}
/** A supplier's serial or EAN kept for a unit instead of a generated number (BR-INVENTORY-009): free text, 1–64 characters, never of the generated shape. */
export const SUPPLIER_SERIAL_MAX_LENGTH = 64;
export const SupplierSerialSchema = z.string().trim().min(1).max(SUPPLIER_SERIAL_MAX_LENGTH)
  .refine((s) => !isSerialNumberShape(s), { message: "has the shape of a serial number, which only Vision X issues", params: { code: "reserved_format" } });
/** A scanned or typed code, 1–64 characters (BR-CATALOGUE-025, BR-INVENTORY-017). */
export const ScannedCodeSchema = z.string().trim().min(1).max(64);

/** W4: stock is whole units, never thousandths. The most units one line or one count entry carries (BR-INVENTORY-027, BR-PURCHASING-015). */
export const LINE_QUANTITY_MAX = 9_999;
/** A line's or a count entry's quantity: a whole number of units, 1–9 999 (W4). */
export const QuantitySchema = z.number().int().min(1).max(LINE_QUANTITY_MAX);
/** A signed whole change of units, never 0, at most 9 999 either way (an adjustment line, BR-INVENTORY-013). */
export const QuantityChangeSchema = z.number().int().min(-LINE_QUANTITY_MAX).max(LINE_QUANTITY_MAX)
  .refine((n) => n !== 0, { message: "a change of no units", params: { code: "zero" } });
/** A count of units held: a whole number, never negative (BR-INVENTORY-001, BR-INVENTORY-003). */
export const UnitCountSchema = z.number().int().min(0);

/** A unit cost ex VAT (W1): whole cents R 0.00–R 999 999.99 (BR-INVENTORY-027, BR-PURCHASING-015). */
export const UNIT_COST_MAX_CENTS = 99_999_999;
export const UnitCostCentsSchema = z.number().int().min(0).max(UNIT_COST_MAX_CENTS).transform((n) => n as Cents);
/** A discount off a list price: 0–10 000 bp, 0–100% (BR-PRICING-019, BR-PURCHASING-007, BR-IMPORTS-023). */
export const DISCOUNT_BP_MAX = 10_000;
export const DiscountBpSchema = z.number().int().min(0).max(DISCOUNT_BP_MAX).transform((n) => n as Bp);
/** A markup percentage: 0–100 000 bp, 0–1 000% (BR-PRICING-004, -007, -019; BR-IMPORTS-023; the cap is Proposed). */
export const MARKUP_BP_MAX = 100_000;
export const MarkupBpSchema = z.number().int().min(0).max(MARKUP_BP_MAX).transform((n) => n as Bp);

/** Stamped on every business record read (§0.4). */
export const AuditStampSchema = z.object({
  createdAt: InstantSchema,
  createdByUserId: UlidSchema.nullable(),
  createdBySystem: z.string().optional(),
  updatedAt: InstantSchema,
  updatedByUserId: UlidSchema.nullable().optional(),
});
export type AuditStamp = z.infer<typeof AuditStampSchema>;
